import * as React from 'react';

/**
 * Context holding the current partner's feature flags as a key→boolean map.
 * Populated by the FeatureFlagProvider in @artic/api-client.
 */
export const FeatureFlagsContext = React.createContext<Record<string, boolean> | null>(null);

function useFeatureFlagsContext(hookName: string): Record<string, boolean> {
  const ctx = React.useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error(`${hookName} must be used within a <FeatureFlagProvider>`);
  }
  return ctx;
}

/**
 * Returns whether the specified feature flag is enabled for the current partner.
 * Returns `false` for unknown flags (safe default).
 *
 * Must be used within a `<FeatureFlagProvider>`.
 */
export function useFeatureFlag(flag: string): boolean {
  const ctx = useFeatureFlagsContext('useFeatureFlag');
  return ctx[flag] ?? false;
}

/**
 * Returns whether any of the specified feature flags are enabled for the
 * current partner. Returns `false` for unknown flags and empty flag lists.
 *
 * Must be used within a `<FeatureFlagProvider>`.
 */
export function useAnyFeatureFlag(flags: readonly string[]): boolean {
  const ctx = useFeatureFlagsContext('useAnyFeatureFlag');
  return flags.some((flag) => ctx[flag] ?? false);
}

/**
 * Renders children only when the specified feature flag is enabled.
 * Optionally renders a fallback when disabled.
 */
export function FeatureFlag({
  flag,
  children,
  fallback = null,
}: {
  flag: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}

/**
 * Renders children when any of the specified feature flags is enabled.
 * Optionally renders a fallback when all are disabled.
 */
export function AnyFeatureFlag({
  flags,
  children,
  fallback = null,
}: {
  flags: readonly string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const enabled = useAnyFeatureFlag(flags);
  return <>{enabled ? children : fallback}</>;
}
